
import React from 'react';
import { formatSlug } from '@/utils/slugUtils';
import { SettingsHeader } from './settings/SettingsHeader';
import { AssessmentStatusToggle } from './settings/AssessmentStatusToggle';
import { MaturityModelStatus } from './settings/MaturityModelStatus';
import { TitleField } from './settings/TitleField';
import { SlugField } from './settings/SlugField';
import { DescriptionField } from './settings/DescriptionField';
import { NotifyEmailsField } from './settings/NotifyEmailsField';
import { CalendlyLinkField } from './settings/CalendlyLinkField';
import { DeleteAssessmentDialog } from './DeleteAssessmentDialog';
import { Assessment } from '@/types/assessment';

interface SettingsTabProps {
  title: string;
  setTitle: (value: string) => void;
  description: string;
  setDescription: (value: string) => void;
  slug: string;
  setSlug: (value: string) => void;
  formatSlug: (value: string) => string;
  isActive?: boolean;
  onStatusChange?: (value: boolean) => void;
  isMaturityModel?: boolean;
  notifyEmails?: string;
  onNotifyEmailsChange?: (value: string) => void;
  calendlyLink?: string;
  onCalendlyLinkChange?: (value: string) => void;
  assessment?: Assessment | null;
}

export function SettingsTab({
  title,
  setTitle,
  description,
  setDescription,
  slug,
  setSlug,
  formatSlug,
  isActive = true,
  onStatusChange,
  isMaturityModel = false,
  notifyEmails = '',
  onNotifyEmailsChange,
  calendlyLink = '',
  onCalendlyLinkChange,
  assessment
}: SettingsTabProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const handleTitleChange = (newTitle: string) => {
    setTitle(newTitle);
    if (!slug) {
      const formattedSlug = formatSlug(newTitle);
      setSlug(formattedSlug);
    }
  };

  const handleSlugChange = (value: string) => {
    const formattedSlug = formatSlug(value);
    setSlug(formattedSlug);
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent default behavior
    // Function can be expanded later if needed
  };

  return (
    <div className="space-y-6">
      <SettingsHeader 
        onButtonClick={handleButtonClick} 
        onDeleteClick={() => setDeleteDialogOpen(true)}
      />

      <div className="space-y-4">
        <AssessmentStatusToggle 
          isActive={isActive} 
          onStatusChange={onStatusChange} 
        />

        <MaturityModelStatus isMaturityModel={isMaturityModel} />

        <TitleField 
          title={title} 
          onTitleChange={handleTitleChange} 
        />

        <SlugField 
          slug={slug} 
          onSlugChange={handleSlugChange} 
        />

        <DescriptionField 
          description={description} 
          onDescriptionChange={setDescription} 
        />

        <NotifyEmailsField 
          notifyEmails={notifyEmails} 
          onNotifyEmailsChange={onNotifyEmailsChange} 
        />
        
        <CalendlyLinkField 
          calendlyLink={calendlyLink} 
          onCalendlyLinkChange={onCalendlyLinkChange} 
        />
      </div>

      {assessment && (
        <DeleteAssessmentDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          assessmentId={assessment.id}
          assessmentTitle={assessment.title}
        />
      )}
    </div>
  );
}
